import { ADMIN_KYC_CASES, ADMIN_ONCHAIN_CLAIM_CASES } from './mockAdapter';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ClaimReviewStatus = 'under_review' | 'approved' | 'rejected' | 'revoked';

export type ClaimType =
  | 'KYC'
  | 'Accredited Investor'
  | 'Qualified Purchaser'
  | 'Institutional Investor'
  | 'PEP'
  | 'Jurisdiction'
  | 'Partner Membership'
  | 'Corporate Email'
  | 'Qualified Premium'
  | 'Other';

export interface ClaimCase {
  id: string;
  facilityName: string;
  type: ClaimType;
  name: string;
  walletAddress: string;
  onchainId: string;
  investorType: 'Individual' | 'Corporate';
  jurisdiction: string;
  email: string;
  /** Source of the attestation data (Sumsub, Onfido, Ascend, …). NOT the issuer. */
  provider: string;
  /** Always 'Ascend' — Ascend is the operator/issuer of every claim per ERC-3643 model. */
  issuer: 'Ascend';
  submittedDate: string;
  riskScore: number;
  reviewStatus: ClaimReviewStatus;
}

// ─── Onchain claim type → unified ClaimType mapping ───────────────────────────
//
// The seed uses snake_case raw claim types (partner_membership, qualified_premium_investor,
// corporate_email_verified, kyc_verified, kyc_eu_resident, institutional_verified, …).
// We collapse them into the canonical ClaimType strings shown in the table.
function normalizeClaimType(raw: string): ClaimType {
  const value = raw.toLowerCase();
  if (value.includes('partner_membership')) return 'Partner Membership';
  if (value.includes('corporate_email')) return 'Corporate Email';
  if (value.includes('qualified_premium')) return 'Qualified Premium';
  if (value.includes('qualified_purchaser')) return 'Qualified Purchaser';
  if (value.includes('institutional')) return 'Institutional Investor';
  if (value.includes('accredited')) return 'Accredited Investor';
  if (value.includes('pep')) return 'PEP';
  if (value.includes('jurisdiction') || value.includes('eu_resident') || value.includes('us_resident')) {
    return 'Jurisdiction';
  }
  if (value.includes('kyc')) return 'KYC';
  // Surface unknowns as 'Other' so a misclassified row is visibly distinct
  // (vs. silently rendering as KYC, which would mask seed-data drift).
  if (typeof console !== 'undefined') {
    console.warn(`[claims] unknown claim type "${raw}" — falling back to 'Other'`);
  }
  return 'Other';
}

// ─── Build the unified, facility-scoped claim rows ────────────────────────────

const kycRows: ClaimCase[] = ADMIN_KYC_CASES.map((row) => ({
  id: row.id,
  facilityName: row.facilityName,
  type: 'KYC' as const,
  name: row.name,
  walletAddress: row.walletAddress,
  onchainId: row.onchainId,
  investorType: row.investorType,
  jurisdiction: row.jurisdiction,
  email: row.email,
  provider: row.provider,
  issuer: 'Ascend' as const,
  submittedDate: row.submittedDate,
  riskScore: row.riskScore,
  reviewStatus: row.reviewStatus,
}));

const onchainRows: ClaimCase[] = ADMIN_ONCHAIN_CLAIM_CASES.map((row) => ({
  id: row.id,
  facilityName: row.facilityName,
  type: normalizeClaimType(row.claimType),
  name: row.name,
  walletAddress: row.walletAddress,
  onchainId: row.onchainId,
  investorType: row.investorType,
  jurisdiction: row.jurisdiction,
  email: row.email,
  provider: row.provider,
  // Hard-override: Ascend is the issuer of every claim, regardless of seed.
  issuer: 'Ascend' as const,
  submittedDate: row.submittedDate,
  riskScore: row.riskScore,
  reviewStatus: row.reviewStatus,
}));

// Custom claims (Partner Membership, Corporate Email, Qualified Premium) are
// already represented in the seed under raw types `partner_membership`,
// `corporate_email_verified`, `qualified_premium_investor` etc., which the
// normalizer above maps to the canonical ClaimType labels. They flow through
// `onchainRows` and need no separate synthesizer.

export const INITIAL_CLAIM_CASES: ClaimCase[] = [...kycRows, ...onchainRows];
